import axios from "axios";
import { call, put, takeLatest } from "redux-saga/effects";
import { buildApiUrl, getAuthHeaders } from "../../../api/apiClient";
import {
  createSensorFailure,
  createSensorStart,
  createSensorSuccess,
  deleteSensorFailure,
  deleteSensorStart,
  deleteSensorSuccess,
  updateSensorFailure,
  updateSensorStart,
  updateSensorSuccess,
} from "../../../store/features/customer/sensor/sensorSlice";
import { fetchInstrumentsStart } from "../../../store/features/customer/instruments/instrumentsSlice";
import {
  fetchSensorsStart,
  upsertSensorSuccess,
} from "../../../store/features/customer/instruments/sensorsSlice";

const URL = buildApiUrl("/api/v1/instruments/sensor/");

const extractApiObject = (payload) => {
  if (payload?.data && !Array.isArray(payload.data)) {
    return payload.data;
  }

  return payload;
};

const buildUpdatedSensorFallback = (payload) => {
  const sensorPayload = payload?.data || {};
  const fallback = {
    id: payload?.sensorId,
  };

  if (sensorPayload.gps_position !== undefined) {
    fallback.gps_position = sensorPayload.gps_position;
  }

  if (sensorPayload.nickname !== undefined) {
    fallback.nickname = sensorPayload.nickname;
  }

  if (sensorPayload.field !== undefined) {
    fallback.field = sensorPayload.field;
  }

  if (sensorPayload.update_frequency !== undefined) {
    fallback.update_frequency = sensorPayload.update_frequency;
  }

  if (sensorPayload.sensor_type !== undefined) {
    fallback.sensor_type = sensorPayload.sensor_type;
  }

  if (sensorPayload.devEUI !== undefined) {
    fallback.devEUI = sensorPayload.devEUI;
  }

  if (sensorPayload.appEUI !== undefined) {
    fallback.appEUI = sensorPayload.appEUI;
  }

  if (sensorPayload.appKey !== undefined) {
    fallback.appKey = sensorPayload.appKey;
  }

  return fallback;
};

const createSensorApi = async ({ data, accessToken }) => {
  try {
    const response = await axios.post(URL, data, {
      headers: getAuthHeaders(accessToken),
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: error.message };
  }
};

function* handleCreateSensorSaga(action) {
  try {
    const data = yield call(createSensorApi, action.payload);
    yield put(createSensorSuccess(data));
  } catch (error) {
    yield put(createSensorFailure(error));
  }
}

const updateSensorApi = async ({ data, sensorId, accessToken }) => {
  try {
    const response = await axios.put(`${URL}${sensorId}/`, data, {
      headers: getAuthHeaders(accessToken),
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: error.message };
  }
};

function* handleUpdateSensorSaga(action) {
  try {
    const data = yield call(updateSensorApi, action.payload);
    yield put(
      upsertSensorSuccess({
        ...buildUpdatedSensorFallback(action.payload),
        ...extractApiObject(data),
      })
    );
    yield put(updateSensorSuccess(data));
  } catch (error) {
    yield put(updateSensorFailure(error));
  }
}

const deleteSensorApi = async ({ sensorId, accessToken }) => {
  try {
    const response = await axios.delete(`${URL}${sensorId}/`, {
      headers: getAuthHeaders(accessToken),
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: error.message };
  }
};

function* handleDeleteSensorSaga(action) {
  try {
    const data = yield call(deleteSensorApi, action.payload);
    yield put(deleteSensorSuccess(data));
    if (action.payload?.accessToken && action.payload?.field) {
      yield put(
        fetchSensorsStart({
          accessToken: action.payload.accessToken,
          field: action.payload.field,
        })
      );
    }
    yield put(
      fetchInstrumentsStart({ accessToken: action.payload.accessToken })
    );
  } catch (error) {
    yield put(deleteSensorFailure(error));
  }
}

export default function* sensorSaga() {
  yield takeLatest(createSensorStart.type, handleCreateSensorSaga);
  yield takeLatest(updateSensorStart.type, handleUpdateSensorSaga);
  yield takeLatest(deleteSensorStart.type, handleDeleteSensorSaga);
}
